import { useState } from 'react';
import clsx from 'clsx';
import type { OptimizedImageProps } from './OptimizedImage.types';
import { Image } from 'lucide-react';

type ImageState = 'loading' | 'loaded' | 'error';

const OBJECT_FIT_MAP = {
  cover: 'object-cover',
  contain: 'object-contain',
  fill: 'object-fill',
  none: 'object-none',
  'scale-down': 'object-scale-down',
} as const;

export function OptimizedImage({
  src,
  alt,
  priority = false,
  aspectRatio,
  width,
  height,
  sizes,
  srcSet,
  fallback,
  className,
  wrapperClassName,
  blur = true,
  objectFit = 'cover',
  ...rest
}: OptimizedImageProps) {
  const [state, setState] = useState<ImageState>('loading');
  const [prevSrc, setPrevSrc] = useState(src);

  if (prevSrc !== src) {
    setPrevSrc(src);
    setState('loading');
  }

  const isLoaded = state === 'loaded';
  const fitClass = OBJECT_FIT_MAP[objectFit];

  if (state === 'error') {
    if (fallback) {
      if (typeof fallback === 'string') {
        return (
          <div
            className={clsx('relative overflow-hidden', wrapperClassName)}
            style={{ aspectRatio: aspectRatio ?? undefined }}
          >
            <img
              src={fallback}
              alt={alt}
              width={width}
              height={height}
              className={clsx('h-auto w-full', fitClass, className)}
            />
          </div>
        );
      }
      return <>{fallback}</>;
    }

    return (
      <div
        className={clsx('relative overflow-hidden bg-gray-200', wrapperClassName)}
        style={{ aspectRatio: aspectRatio ?? undefined }}
      >
        <div className='flex h-full w-full items-center justify-center'>
          <Image size={32} className='text-gray-500' />
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        'relative overflow-hidden',
        !isLoaded && blur && 'bg-gray-200',
        wrapperClassName
      )}
      style={{ aspectRatio: aspectRatio ?? undefined }}
    >
      {src ? (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          sizes={sizes}
          srcSet={srcSet}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : undefined}
          onLoad={() => setState('loaded')}
          onError={() => setState('error')}
          className={clsx(
            'h-full w-full',
            blur && 'transition-opacity duration-300',
            blur && (isLoaded ? 'opacity-100' : 'opacity-0'),
            fitClass,
            className
          )}
          {...rest}
        />
      ) : (
        <div className='flex h-full w-full items-center justify-center'>
          <Image size={32} className='text-gray-500' />
        </div>
      )}
    </div>
  );
}
